// LT-552 (LT-536): Campaign-from-Brief review page. The mandatory review +
// smoke-test surface for the upload path (decision #17). Left pane = the
// operator's brief (read-only); right pane = the generated Agent persona +
// Campaign fields (read-only). "Speak to Agent" embeds LT-520's WebRTC test
// against the draft agent. LT-553 adds the natural-language tweaks loop here;
// Launch (LT-555) lands later — an interim route to the cockpit stands in for
// now. Wizard campaigns don't belong here and are redirected to their cockpit.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Mic, Rocket, Users } from 'lucide-react';

import PageShell from '../../components/PageShell';
import { Button, Card, Callout, Pill, Textarea } from '../../ui';
import WebTestAgentModal from '../../components/WebTestAgentModal';
import ContactPickerModal from '../../components/ContactPickerModal';
import { campaignsAPI, extractCampaignApiError } from '../../services/campaignsAPI';
import { agentsService } from '../../services/agentsService';
import { contactListsAPI } from '../../services/contactListsAPI';
import { campaignBriefAPI, extractErrorMessage } from '../../services/api';
import { createStaticListFromContacts, pickedListName } from '../../utils/audienceList';
import type { Campaign } from '../../types/campaign';
import type { Agent } from '../../types/agent';
import type { Contact } from '../../types/contact';

// Mirrors backend brief_extraction.MAX_TWEAKS. Only used to seed the initial
// "remaining" count from the loaded campaign; the server is authoritative and
// returns tweaks_remaining on every apply.
const TWEAK_LIMIT = 5;

const Field: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] font-medium tracking-wider uppercase text-slate-400 mb-0.5">
        {label}
      </div>
      <p className="text-sm text-slate-200 whitespace-pre-wrap">{value}</p>
    </div>
  );
};

const ListField: React.FC<{ label: string; items?: string[] | null }> = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] font-medium tracking-wider uppercase text-slate-400 mb-1">
        {label}
      </div>
      <ul className="list-disc pl-5 space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-200">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
};

// LT-570: KPIs extracted from the brief's success-metrics section, read-only.
const KpiField: React.FC<{ kpis?: { name: string; target_value: number; unit: string }[] | null }> = ({
  kpis,
}) => {
  if (!kpis || kpis.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] font-medium tracking-wider uppercase text-slate-400 mb-1">
        KPIs to track
      </div>
      <ul className="space-y-0.5">
        {kpis.map((k, i) => (
          <li key={i} className="text-sm text-slate-200">
            {k.name} — <span className="font-mono">{k.target_value}</span> {k.unit}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CampaignReviewPage: React.FC = () => {
  const { campaignId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultsNote = Boolean((location.state as { defaultsNote?: boolean } | null)?.defaultsNote);

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  // LT-555: launch confirmation gate (upload path).
  const [contactCount, setContactCount] = useState<number | null>(null);
  // Audience step — the upload path drafts an agent + campaign from the brief
  // but never enrols an audience, so the launch gate below is unsatisfiable
  // without this. Picked contacts are materialised as a static list (Approach
  // A, shared with LT-534) and set as the campaign's contact_list_id.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savingAudience, setSavingAudience] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  // LT-553: tweaks loop.
  const [tweakText, setTweakText] = useState('');
  const [applyingTweak, setApplyingTweak] = useState(false);
  const [tweaksRemaining, setTweaksRemaining] = useState<number>(TWEAK_LIMIT);
  const [tweakMessage, setTweakMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const c = await campaignsAPI.get(campaignId);
        if (cancelled) return;
        if (c.creation_source !== 'upload') {
          // Path lock: wizard campaigns edit via the wizard form, not here.
          navigate(`/campaigns/${campaignId}`, { replace: true });
          return;
        }
        setCampaign(c);
        const usedTweaks = (c.brief_versions ?? []).filter((v) => v.source === 'tweak').length;
        setTweaksRemaining(Math.max(0, TWEAK_LIMIT - usedTweaks));
        if (c.agent_id) {
          const a = await agentsService.get(c.agent_id).catch(() => null);
          if (!cancelled) setAgent(a);
        }
        // LT-555: audience size for the launch gate.
        if (c.contact_list_id) {
          const ev = await contactListsAPI.evaluate(c.contact_list_id).catch(() => null);
          if (!cancelled) setContactCount(ev?.member_count ?? 0);
        } else if (!cancelled) {
          setContactCount(0);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(extractErrorMessage(err, 'Could not load this campaign.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [campaignId, navigate]);

  const brief = useMemo(() => {
    const versions = campaign?.brief_versions ?? [];
    return versions.length ? versions[versions.length - 1] : null;
  }, [campaign]);

  const version = agent?.current_version ?? null;

  const handleApplyTweak = async () => {
    const t = tweakText.trim();
    if (!t || !campaign) return;
    setApplyingTweak(true);
    setTweakMessage(null);
    try {
      const result = await campaignBriefAPI.applyTweak(campaign.id, t);
      // Refresh the agent (new version persona) + campaign brief fields so
      // the page — and the next "Speak to Agent" — reflect the tweak.
      if (campaign.agent_id) {
        const a = await agentsService.get(campaign.agent_id).catch(() => null);
        setAgent(a);
      }
      setCampaign((prev) =>
        prev ? ({ ...prev, ...(result.draft_campaign as Partial<Campaign>) }) : prev,
      );
      setTweaksRemaining(result.tweaks_remaining ?? Math.max(0, tweaksRemaining - 1));
      setTweakText('');
      setTweakMessage({ type: 'success', text: 'Applied to the drafted agent & campaign on the right (your uploaded brief stays as-is). Speak to the agent again to hear the change.' });
    } catch (err: unknown) {
      setTweakMessage({ type: 'error', text: extractErrorMessage(err, 'Could not apply that tweak.') });
    } finally {
      setApplyingTweak(false);
    }
  };

  const handlePickContacts = async (contacts: Contact[]) => {
    if (!campaign || contacts.length === 0) return;
    setSavingAudience(true);
    setAudienceError(null);
    try {
      const ids = contacts.map((c) => c.id);
      const list = await createStaticListFromContacts(
        pickedListName(campaign.name, ids.length),
        ids,
      );
      await campaignsAPI.update(campaign.id, { contact_list_id: list.id });
      setCampaign((prev) => (prev ? { ...prev, contact_list_id: list.id } : prev));
      setContactCount(ids.length);
      setPickerOpen(false);
    } catch (err: unknown) {
      setAudienceError(extractCampaignApiError(err, 'Could not set the audience.'));
    } finally {
      setSavingAudience(false);
    }
  };

  // LT-555: launch gate prerequisites. No auto-launch (decision #18) — the
  // operator must open the summary and click Launch explicitly.
  const hasAgent = Boolean(campaign?.agent_id);
  const hasContacts = (contactCount ?? 0) > 0;
  const launchReady = hasAgent && hasContacts;
  const launchBlockedReason = !hasAgent
    ? 'This campaign has no agent yet.'
    : !hasContacts
      ? 'No contacts to call yet — add an audience before launching.'
      : null;

  const handleLaunch = async () => {
    if (!campaign) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      await campaignsAPI.transition(campaign.id, 'active');
      navigate(`/campaigns/${campaign.id}`);
    } catch (err: unknown) {
      setLaunchError(extractCampaignApiError(err, 'Launch failed.'));
    } finally {
      setLaunching(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <p className="text-sm text-slate-400">Loading review…</p>
      </PageShell>
    );
  }
  if (error || !campaign) {
    return (
      <PageShell>
        <Callout tone="danger">{error ?? 'Campaign not found.'}</Callout>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <header className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/campaigns')}
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 mb-3"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          Campaigns
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
              Review &amp; launch
            </div>
            <h1 className="text-2xl font-semibold text-slate-50 flex items-center gap-2">
              {campaign.name}
              <Pill intent="info">Upload</Pill>
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Here&apos;s what we drafted from your brief. Hear the agent before you launch — then
              launch when it sounds right.
            </p>
          </div>
          <Button intent="secondary" onClick={() => setTestOpen(true)} data-testid="speak-to-agent">
            <Mic size={16} strokeWidth={2.5} />
            Speak to Agent
          </Button>
        </div>
      </header>

      {defaultsNote ? (
        <Callout tone="warn" className="mb-4">
          Some questions were resolved with defaults — review the draft carefully before launch.
        </Callout>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Left: the operator's original upload (read-only — never rewritten,
            even by tweaks; it's the audit record of what was submitted). */}
        <Card elevation="raised" className="flex flex-col gap-3">
          <div>
            <h2 className="text-base font-medium text-white">Your uploaded brief</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              The document you uploaded, kept as-is for reference. Tweaks refine
              the drafted agent &amp; campaign on the right — they don&apos;t edit
              this original.
            </p>
          </div>
          {brief ? (
            <>
              <div className="flex flex-wrap gap-2">
                {brief.frontmatter?.voice_gender ? (
                  <Pill intent="neutral">Voice: {String(brief.frontmatter.voice_gender)}</Pill>
                ) : null}
                {brief.frontmatter?.voice_language ? (
                  <Pill intent="neutral">Language: {String(brief.frontmatter.voice_language)}</Pill>
                ) : null}
              </div>
              <p className="text-sm text-slate-300 whitespace-pre-wrap">{brief.narrative}</p>
            </>
          ) : (
            <p className="text-sm text-slate-500">No brief content captured.</p>
          )}
        </Card>

        {/* Right: generated agent + campaign (read-only). */}
        <div className="flex flex-col gap-4">
          <Card elevation="raised" className="flex flex-col gap-3">
            <h2 className="text-base font-medium text-white">Agent persona</h2>
            <Field label="Name" value={agent?.name} />
            <Field label="Tone" value={version?.tone} />
            <Field label="Persona" value={version?.agent_persona} />
            <Field label="Objection handling" value={version?.objection_handling_style} />
            {!agent ? (
              <p className="text-sm text-slate-500">Agent details unavailable.</p>
            ) : null}
          </Card>

          <Card elevation="raised" className="flex flex-col gap-3">
            <h2 className="text-base font-medium text-white">Campaign</h2>
            <Field label="Purpose" value={campaign.purpose} />
            <Field label="Target audience" value={campaign.target_audience} />
            <Field label="Call objectives" value={campaign.call_objectives} />
            <Field label="Goals" value={campaign.campaign_goals} />
            <KpiField kpis={campaign.kpis} />
            <ListField label="Likely objections" items={campaign.likely_objections} />
            <ListField label="Scripts" items={campaign.scripts} />
          </Card>
        </div>
      </div>

      {/* Audience — the brief drafts the agent + campaign, but the operator
          still has to choose who to call. Without this the launch gate below
          can never be satisfied on the upload path. */}
      <Card elevation="raised" className="mt-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-medium text-white flex items-center gap-2">
              <Users size={16} strokeWidth={2.5} className="text-accent" />
              Audience
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {hasContacts
                ? `${(contactCount ?? 0).toLocaleString()} contact${contactCount === 1 ? '' : 's'} will be called when you launch.`
                : 'Choose the contacts this campaign should call.'}
            </p>
          </div>
          <Button
            intent={hasContacts ? 'secondary' : 'primary'}
            onClick={() => { setAudienceError(null); setPickerOpen(true); }}
            data-testid="choose-audience"
          >
            <Users size={16} strokeWidth={2.5} />
            {hasContacts ? 'Change contacts' : 'Choose contacts'}
          </Button>
        </div>
        {audienceError ? <Callout tone="danger">{audienceError}</Callout> : null}
      </Card>

      {/* LT-553: natural-language tweaks loop. */}
      <Card elevation="flat" className="mt-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-white">Refine with a tweak</h2>
          <span className="text-xs text-slate-500">
            {tweaksRemaining} of {TWEAK_LIMIT} left
          </span>
        </div>
        <p className="text-sm text-slate-400">
          Describe a change in plain language — e.g. &ldquo;say &lsquo;cloud journey&rsquo;, not
          &lsquo;cloud business&rsquo;&rdquo;. We regenerate the agent; no prompt editing.
        </p>
        {tweakMessage ? (
          <Callout tone={tweakMessage.type === 'success' ? 'ok' : 'danger'}>{tweakMessage.text}</Callout>
        ) : null}
        <Textarea
          id="brief-tweak"
          rows={3}
          placeholder="What should change?"
          value={tweakText}
          onChange={(e) => setTweakText(e.target.value)}
          disabled={applyingTweak || tweaksRemaining <= 0}
        />
        <div className="flex justify-end">
          <Button
            intent="secondary"
            onClick={handleApplyTweak}
            disabled={applyingTweak || tweaksRemaining <= 0 || !tweakText.trim()}
            data-testid="apply-tweak"
          >
            {applyingTweak ? 'Applying…' : 'Apply tweak'}
          </Button>
        </div>
        {tweaksRemaining <= 0 ? (
          <p className="text-xs text-warn-text">
            Tweak limit reached — pause and recreate the campaign for bigger changes.
          </p>
        ) : null}
      </Card>

      {/* LT-555: launch confirmation gate. Explicit click + summary; no
          auto-launch. Disabled until the campaign has an agent + contacts. */}
      <div className="mt-6 flex flex-col items-end gap-2">
        {launchBlockedReason ? (
          <span className="text-xs text-warn-text">{launchBlockedReason}</span>
        ) : null}
        <Button
          intent="primary"
          onClick={() => { setLaunchError(null); setLaunchOpen(true); }}
          disabled={!launchReady}
          data-testid="open-launch"
        >
          <Rocket size={16} strokeWidth={2.5} />
          Review &amp; launch
        </Button>
      </div>

      {launchOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Launch confirmation"
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
        >
          <Card elevation="raised" className="w-full max-w-md flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-slate-50">Launch this campaign?</h2>
            <p className="text-sm text-slate-400">
              This starts calling real contacts. Check the summary, then launch.
            </p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-slate-400">Campaign</dt>
              <dd className="text-slate-100">{campaign.name}</dd>
              <dt className="text-slate-400">Agent</dt>
              <dd className="text-slate-100">
                {agent?.name ?? '—'}
                {version ? ` · v${version.version_number}` : ''}
              </dd>
              <dt className="text-slate-400">Contacts</dt>
              <dd className="text-slate-100">{(contactCount ?? 0).toLocaleString()}</dd>
              <dt className="text-slate-400">Retries</dt>
              <dd className="text-slate-100">
                {campaign.retry_count} over {(campaign.retry_delays_days ?? []).join('/')}d
              </dd>
            </dl>
            {launchError ? <Callout tone="danger">{launchError}</Callout> : null}
            <div className="flex justify-end gap-2">
              <Button intent="ghost" onClick={() => setLaunchOpen(false)} disabled={launching}>
                Cancel
              </Button>
              <Button
                intent="primary"
                onClick={handleLaunch}
                disabled={launching || !launchReady}
                data-testid="confirm-launch"
              >
                {launching ? 'Launching…' : `Launch · ${(contactCount ?? 0).toLocaleString()} will enrol`}
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      <WebTestAgentModal isOpen={testOpen} onClose={() => setTestOpen(false)} campaignId={campaignId} />

      <ContactPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickContacts}
        title="Choose this campaign's audience"
        description="Search and tick the contacts to call. Your selection becomes the campaign's audience."
        confirmLabel="Set audience"
        busy={savingAudience}
        error={audienceError}
      />
    </PageShell>
  );
};

export default CampaignReviewPage;
